import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button, Label, Spinner, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@racing/ui';

export interface AssignmentItem {
  id: number;
  primary: string;
  secondary?: string | null;
}

export interface AssignmentOption {
  id: number;
  label: string;
}

interface AssignmentListProps {
  items: AssignmentItem[];
  /** Already filtered to valid, not-yet-assigned choices. */
  options: AssignmentOption[];
  addLabel: string;
  emptyMessage: string;
  loading?: boolean;
  onAdd: (id: number) => void;
  onRemove: (id: number) => void;
}

/**
 * A list of things assigned to something, with a control to add more.
 *
 * One component for all four of these: users on a track, users on a
 * sanctioning body, tracks on a user, sanctioning bodies on a user. The four
 * were previously written out separately and had drifted — different empty
 * text, different control sizes, and only some of the remove buttons carried an
 * accessible name.
 *
 * These lists are an authorization surface: they are exactly what
 * `hasTrackAccess` and `hasEventAccess` consult on the server. The secondary
 * line matters for that reason — it is how someone tells two similarly named
 * records apart before removing the wrong one.
 */
const AssignmentList = ({
  items,
  options,
  addLabel,
  emptyMessage,
  loading = false,
  onAdd,
  onRemove,
}: AssignmentListProps) => {
  const [selected, setSelected] = useState('');
  const selectId = `assign-${addLabel.replace(/\s+/g, '-').toLowerCase()}`;

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.length === 0 ? (
        <p className="rounded-md border border-dashed border-line-strong px-3 py-4 text-center font-sans text-sm text-ink-faint">
          {emptyMessage}
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-3 rounded-md border border-line px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-sans text-base text-ink">{item.primary}</p>
                {item.secondary && (
                  <p className="truncate font-sans text-xs text-ink-faint">{item.secondary}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove ${item.primary}`}
                className="text-danger hover:bg-danger-bg"
                onClick={() => onRemove(item.id)}
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {options.length > 0 && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex min-w-[14rem] flex-1 flex-col gap-1.5">
            <Label htmlFor={selectId}>{addLabel}</Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger id={selectId}>
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.id} value={String(option.id)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            disabled={!selected}
            onClick={() => {
              onAdd(Number(selected));
              setSelected('');
            }}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

export default AssignmentList;
